import { type MedicalRecord } from '@/api';

interface TimelineEvent {
  id: string;
  type: 'appointment' | 'diagnosis' | 'prescription' | 'exam' | 'certificate' | 'evolution' | 'attachment';
  title: string;
  description: string;
  date: string;
  icon: 'appointment' | 'diagnosis' | 'prescription' | 'exam' | 'certificate' | 'evolution' | 'attachment';
}

interface MedicalTimelineProps {
  medicalRecord: MedicalRecord;
  events?: TimelineEvent[];
  maxItems?: number;
}

const iconMap = {
  appointment: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  diagnosis: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  prescription: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  exam: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  certificate: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  ),
  evolution: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  attachment: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  ),
};

const colorMap: Record<TimelineEvent['type'], string> = {
  appointment: 'bg-blue-100 text-blue-600',
  diagnosis: 'bg-red-100 text-red-600',
  prescription: 'bg-green-100 text-green-600',
  exam: 'bg-purple-100 text-purple-600',
  certificate: 'bg-amber-100 text-amber-600',
  evolution: 'bg-indigo-100 text-indigo-600',
  attachment: 'bg-gray-100 text-gray-600',
};

export function MedicalTimeline({ medicalRecord, events = [], maxItems = 10 }: MedicalTimelineProps) {
  const timelineEvents: TimelineEvent[] = events.length > 0
    ? events
    : buildTimelineFromRecord(medicalRecord);

  const displayedEvents = timelineEvents.slice(0, maxItems);

  if (displayedEvents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">Nenhum evento registrado na timeline.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

      <div className="space-y-4">
        {displayedEvents.map((event, index) => (
          <div key={event.id || index} className="relative flex items-start gap-4">
            <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${colorMap[event.type]} flex items-center justify-center`}>
              {iconMap[event.icon]}
            </div>

            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">{event.title}</p>
                <p className="text-xs text-gray-500">
                  {new Date(event.date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <p className="text-sm text-gray-600 mt-0.5">{event.description}</p>
            </div>
          </div>
        ))}
      </div>

      {timelineEvents.length > maxItems && (
        <div className="mt-4 text-center">
          <span className="text-sm text-gray-500">
            + {timelineEvents.length - maxItems} evento(s) não mostrado(s)
          </span>
        </div>
      )}
    </div>
  );
}

function buildTimelineFromRecord(record: MedicalRecord): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  events.push({
    id: `created-${record.id}`,
    type: 'appointment',
    title: 'Prontuário Criado',
    description: `Prontuário iniciado em ${record.appointmentId}`,
    date: record.createdAt,
    icon: 'appointment',
  });

  if (record.diagnosisIds && record.diagnosisIds.length > 0) {
    events.push({
      id: `diagnoses-${record.id}`,
      type: 'diagnosis',
      title: 'Diagnósticos Registrados',
      description: `${record.diagnosisIds.length} diagnóstico(s) adicionado(s)`,
      date: record.updatedAt,
      icon: 'diagnosis',
    });
  }

  if (record.prescriptionIds && record.prescriptionIds.length > 0) {
    events.push({
      id: `prescriptions-${record.id}`,
      type: 'prescription',
      title: 'Prescrições Emitidas',
      description: `${record.prescriptionIds.length} prescrição(ões) emitida(s)`,
      date: record.updatedAt,
      icon: 'prescription',
    });
  }

  if (record.examRequestIds && record.examRequestIds.length > 0) {
    events.push({
      id: `exams-${record.id}`,
      type: 'exam',
      title: 'Exames Solicitados',
      description: `${record.examRequestIds.length} solicitação(ões) de exame(s)`,
      date: record.updatedAt,
      icon: 'exam',
    });
  }

  if (record.certificateIds && record.certificateIds.length > 0) {
    events.push({
      id: `certificates-${record.id}`,
      type: 'certificate',
      title: 'Certificados Emitidos',
      description: `${record.certificateIds.length} certificado(s) emitido(s)`,
      date: record.updatedAt,
      icon: 'certificate',
    });
  }

  if (record.evolutionIds && record.evolutionIds.length > 0) {
    events.push({
      id: `evolutions-${record.id}`,
      type: 'evolution',
      title: 'Evoluções Clínicas',
      description: `${record.evolutionIds.length} evolução(ões) registrada(s)`,
      date: record.updatedAt,
      icon: 'evolution',
    });
  }

  if (record.attachmentIds && record.attachmentIds.length > 0) {
    events.push({
      id: `attachments-${record.id}`,
      type: 'attachment',
      title: 'Anexos Adicionados',
      description: `${record.attachmentIds.length} anexo(s) adicionado(s)`,
      date: record.updatedAt,
      icon: 'attachment',
    });
  }

  if (record.signedAt) {
    events.push({
      id: `signed-${record.id}`,
      type: 'certificate',
      title: 'Prontuário Assinado',
      description: `Assinado por ${record.signedBy}`,
      date: record.signedAt,
      icon: 'certificate',
    });
  }

  return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}
